import { useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FilterResearchTypeOperation from 'practice/components/type-operation/research/filter';
import TableTypeOperation from 'practice/components/type-operation/research/table-type-operation';
import { validationSchema } from 'practice/constants/research-type-operation-schema';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PracticeAuthorizations } from 'shell/authorizations/practice';
import { Form, Formik } from 'formik';
import { ResearchTypeOperationCrubs } from 'practice/constants/type-operation-crubs';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { TypeOperationData } from 'shell/models/type-operation';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformDataset } from './utils';
import {
  BodyResearchTypeOperation,
  ResearchTypeOperationVM,
} from 'practice/view-models/research-type-operation';

export const authsObj = Object.freeze({
  read: [PracticeAuthorizations.RicercaTipoOperazione],
  create: [PracticeAuthorizations.NuovoTipoOperazione],
  edit: [PracticeAuthorizations.ModificaTipoOperazione],
  delete: [PracticeAuthorizations.EliminaTipoOperazione],
});

const ResearchTypeOperation = (): ReactElement => {
  const navigate = useNavigate();

  const checkFlagStampa = (value: string | undefined) => {
    if (value) {
      if (value === '1') {
        return 'SI';
      } else if (value === '0') {
        return 'NO';
      }
    } else return '';
  };

  const [typeOperation, getTypeOperation] = useHttpFetch<
    HttpResponseList<TypeOperationData>,
    Array<BodyResearchTypeOperation>
  >(`${__PRACTICE_API_URL__}/pratiche/tipo-operazione`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponseList<TypeOperationData>>) => {
      const { data } = response;
      return data?.payload?.list?.map((item) => ({
        code: item.key?.tipoOperazione || '',
        description: item.descrizioneTipoOperazione || '',
        descriptionBrief: item.descrizioneBreveTipoOperazione || '',
        practice: item.key?.tipoPratica || '',
        bodyCode: item.key?.codiceEnte || '',
        printDoc: checkFlagStampa(item.flagStampaDocumento || undefined),
        typeDocument: item.codiceTipoDocumento || '',
        inUse: checkFlagStampa(item.flagOperazioneInUso || undefined),
      }));
    },
  });

  const deleteTypeOperation = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/tipo-operazione`, {
    options: { method: 'DELETE' },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchTypeOperation>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<
    AlertModal<BodyResearchTypeOperation>
  >({
    element: null,
    open: false,
  });

  const auths = useActionAuthorization(authsObj);

  const onSubmitFilter = useEventCallback((values: ResearchTypeOperationVM) => {
    const queryString = transformDataset(values);

    getTypeOperation({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchTypeOperationCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo tipo operazione"
        addBtnLabel="Aggiungi nuovo tipo operazione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/pratiche/ricerca-tipo-operazione/nuovo');
        }}
        title="Ricerca Tipo Operazione"
      />
      <div className="circle" />
      <Formik
        initialValues={{
          filter: {
            code: '',
            description: '',
            typePractice: '',
            typeDocument: '',
            inUse: '1',
            orders: {},
          },
        }}
        onSubmit={onSubmitFilter}
        validationSchema={validationSchema}
      >
        <Form>
          <FilterResearchTypeOperation />
          {typeOperation ? (
            <TableTypeOperation
              activeDelete={auths.delete}
              activeEdit={auths.edit}
              getTypeOperation={getTypeOperation}
              typeOperation={typeOperation.data as Array<BodyResearchTypeOperation>}
              setDeleteAlertModal={setDeleteAlertModal}
            />
          ) : undefined}
        </Form>
      </Formik>
      <DeleteAlertModal
        title="Cancellazione Tipo Operazione"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteTypeOperation({
              url: `/${deleteAlertModal.element?.practice}/${deleteAlertModal.element?.code}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Tipo Operazione"
        message={`La cancellazione della funzione ${deleteSuccessModal.element?.code} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter({
            filter: {
              code: '',
              description: '',
              typePractice: '',
              typeDocument: '',
              inUse: '1',
              orders: {},
            },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter({
            filter: {
              code: '',
              description: '',
              typePractice: '',
              typeDocument: '',
              inUse: '1',
              orders: {},
            },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ResearchTypeOperation.displayName = 'Type Operation Page component';

export default ResearchTypeOperation;
